import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Box, CircularProgress, FileSelectorFileType } from '@parspec/pixel';

import { TAB_OPTIONS } from '../../ImportMoreProducts/shared/constants';
import FileSelector from './uploadAndSelectFile';
import TabsList from './Tabs';
import { useGetProjectFoldersAndFiles } from 'src/features/Project/queries';
import { UploadedFilePath } from '../types';
import ProjectsFilesTable from 'src/features/shared/ProjectsTable/ProjectFilesTable';
import { NestedFolderResponse } from 'src/features/Project/queries/apiTypes';

interface IFileType {
    file: FileSelectorFileType;
    s3_file_path?: string;
}

interface TabsContentProps {
    radioInputValue?: string;
    fileRequiredError?: string;
    onUpload?: (files: IFileType[]) => void;
    onSelect?: (files: FileSelectorFileType[]) => void;
    setUploadedFilePath?: Dispatch<SetStateAction<UploadedFilePath | null>>;
    setFileRequiredError?: Dispatch<SetStateAction<string | undefined>>;
    setUploadedFile?: Dispatch<SetStateAction<IFileType[] | null>>;
    fileUploadWarning?: string;
    uploadedFile?: IFileType[] | null;
    acceptedFileTypes?: string[];
    onSelectionChanged?: (param: any) => void;
}

const FilesTabsWrapper: React.FC<TabsContentProps> = ({ radioInputValue, onSelect, onUpload, setUploadedFilePath, setUploadedFile, fileUploadWarning, uploadedFile, acceptedFileTypes, onSelectionChanged }) => {
    const { projectId } = useParams();
    const {
        data: projectFoldersAndFiles,
        isLoading: isLoadingProjectFoldersAndFiles,
    } = useGetProjectFoldersAndFiles(Number(projectId));

    const [isTabManuallyChanged, setIsTabManuallyChanged] = useState(false);
    const [pathName, setPathName] = useState(TAB_OPTIONS[0].value)
    const [key, setKey] = useState<number>()


    const filteredFolders = useMemo(() => {
        if (!projectFoldersAndFiles?.data || !acceptedFileTypes) return [];

        // Filter the folders and files based on the accepted file types
        return projectFoldersAndFiles.data.reduce<NestedFolderResponse[]>((acc, folder) => {
            const filteredDocuments = folder.documents.filter(
                file => acceptedFileTypes.includes(`.${file.file_type}`)
            );

            if (filteredDocuments.length > 0) {
                acc.push({
                    ...folder,
                    documents: filteredDocuments,
                    document_count: filteredDocuments.length
                });
            }
            return acc;
        }, []);
    }, [projectFoldersAndFiles, acceptedFileTypes])

    useEffect(() => {
        if (radioInputValue || acceptedFileTypes) {
            if (filteredFolders?.length === 0 && !isTabManuallyChanged) {
                setPathName(TAB_OPTIONS[1].value);
            } else {
                setPathName(TAB_OPTIONS[0].value)
            }
        }
    }, [projectFoldersAndFiles]);

    useEffect(() => {
        const fileType = uploadedFile && uploadedFile[0]?.file?.path;
        const fileExtension = `.${fileType?.split('.').pop()}`;
        if (fileType && !acceptedFileTypes?.includes(fileExtension)) {
            const newKey = Date.now();
            setKey(newKey);
        }
    }, [radioInputValue])

    const handleTabChange = (tabValue: string) => {
        setPathName(tabValue);
        setIsTabManuallyChanged(true);
        setUploadedFilePath?.(null)
        setUploadedFile?.(null)
    };

    const tabContent = {
        [TAB_OPTIONS[0].value]: (
            <Box marginTop="8px">
                <ProjectsFilesTable
                    onSelectionChanged={onSelectionChanged}
                    nestedFolders={filteredFolders}
                    hideContextMenu
                    tableHeight={200}
                    noFilesText='No compatible files to show'
                />
            </Box>
        ),
        [TAB_OPTIONS[1].value]: (
            <FileSelector
                key={key}
                radioInputValue={radioInputValue}
                onUpload={onUpload}
                onSelect={onSelect}
                warning={fileUploadWarning}
            />
        ),
    };

    const getTabContent = () => {
        return tabContent[pathName];
    };
    return (
        <Box width="100%">
            {isLoadingProjectFoldersAndFiles || !pathName ?
                (<Box display="flex" minHeight={200} alignItems="center" justifyContent="center">
                    <CircularProgress color="primary" size="sm" />
                </Box>) :
                (<Box>
                    <>
                        <TabsList
                            pathName={pathName}
                            handleTabChange={handleTabChange}
                        />
                        {getTabContent()}
                    </>
                </Box>)
            }

        </Box>
    )
};

export default FilesTabsWrapper;
